import {
  GUIDED_TOURS_FETCH_ERROR,
  GUIDED_TOURS_FETCH_START,
  GUIDED_TOURS_FETCH_SUCCESS,
  GUIDED_TOURS_HYDRATE,
  GUIDED_TOURS_MARK_COMPLETED,
  GUIDED_TOURS_SYNC_FAILED,
  GUIDED_TOURS_SYNC_SUCCEEDED,
  USER_LOGOUT,
} from '@core/constants/actions';
import type { GuidedToursMap } from '@app/utils/guidedTourStorage';
import {
  markGuidedTourCompletedInMap,
  writeGuidedToursToStorage,
} from '@app/utils/guidedTourStorage';

export type PendingTourSync = {
  tourId: string;
  variant: string;
  attempts: number;
};

export type GuidedToursStatus = 'idle' | 'loading' | 'ready' | 'error';

export type GuidedToursState = {
  tours: GuidedToursMap;
  status: GuidedToursStatus;
  userId: string | null;
  pendingSync: PendingTourSync[];
};

const initialState: GuidedToursState = {
  tours: {},
  status: 'idle',
  userId: null,
  pendingSync: [],
};

type GuidedToursAction =
  | {
      type: typeof GUIDED_TOURS_HYDRATE;
      userId: string;
      tours: GuidedToursMap;
    }
  | { type: typeof GUIDED_TOURS_FETCH_START }
  | {
      type: typeof GUIDED_TOURS_FETCH_SUCCESS;
      tours: GuidedToursMap;
    }
  | { type: typeof GUIDED_TOURS_FETCH_ERROR }
  | {
      type: typeof GUIDED_TOURS_MARK_COMPLETED;
      tourId: string;
      variant: string;
    }
  | {
      type: typeof GUIDED_TOURS_SYNC_FAILED;
      tourId: string;
      variant: string;
    }
  | {
      type: typeof GUIDED_TOURS_SYNC_SUCCEEDED;
      tourId: string;
      variant: string;
      tours: GuidedToursMap;
    }
  | { type: typeof USER_LOGOUT; status: 'success' };

const upsertPendingSync = (
  pendingSync: PendingTourSync[],
  tourId: string,
  variant: string,
  attempts: number
): PendingTourSync[] => {
  const existing = pendingSync.find(
    (entry) => entry.tourId === tourId && entry.variant === variant
  );

  if (existing) {
    return pendingSync.map((entry) =>
      entry.tourId === tourId && entry.variant === variant
        ? { ...entry, attempts }
        : entry
    );
  }

  return [...pendingSync, { tourId, variant, attempts }];
};

const removePendingSync = (
  pendingSync: PendingTourSync[],
  tourId: string,
  variant: string
): PendingTourSync[] =>
  pendingSync.filter(
    (entry) => !(entry.tourId === tourId && entry.variant === variant)
  );

const guidedToursReducer = (
  state = initialState,
  action: GuidedToursAction
): GuidedToursState => {
  switch (action.type) {
    case GUIDED_TOURS_HYDRATE: {
      const nextState = {
        ...state,
        userId: action.userId,
        tours: action.tours,
      };
      writeGuidedToursToStorage(action.userId, action.tours);
      return nextState;
    }

    case GUIDED_TOURS_FETCH_START:
      return {
        ...state,
        status: 'loading',
      };

    case GUIDED_TOURS_FETCH_SUCCESS: {
      const nextState = {
        ...state,
        status: 'ready' as const,
        tours: action.tours,
      };
      if (state.userId) {
        writeGuidedToursToStorage(state.userId, action.tours);
      }
      return nextState;
    }

    case GUIDED_TOURS_FETCH_ERROR:
      return {
        ...state,
        status: 'error',
      };

    case GUIDED_TOURS_MARK_COMPLETED: {
      const tours = markGuidedTourCompletedInMap(
        state.tours,
        action.tourId,
        action.variant
      );
      if (state.userId) {
        writeGuidedToursToStorage(state.userId, tours);
      }
      return {
        ...state,
        tours,
      };
    }

    case GUIDED_TOURS_SYNC_FAILED:
      return {
        ...state,
        pendingSync: upsertPendingSync(
          state.pendingSync,
          action.tourId,
          action.variant,
          (state.pendingSync.find(
            (entry) =>
              entry.tourId === action.tourId && entry.variant === action.variant
          )?.attempts ?? 0) + 1
        ),
      };

    case GUIDED_TOURS_SYNC_SUCCEEDED: {
      const nextState = {
        ...state,
        tours: action.tours,
        pendingSync: removePendingSync(
          state.pendingSync,
          action.tourId,
          action.variant
        ),
      };
      if (state.userId) {
        writeGuidedToursToStorage(state.userId, action.tours);
      }
      return nextState;
    }

    case USER_LOGOUT:
      if (action.status !== 'success') {
        return state;
      }
      return initialState;

    default:
      return state;
  }
};

export default guidedToursReducer;
